<template>
  <div v-if="mostrar" class="modal-overlay">
    <!-- Renomeado para modal-container -->
    <div class="modal-container">
      <!-- Cabeçalho com Botões de Alternância e Fechar -->
      <div class="modal-header">
        <h5>Pesquisa de pessoas</h5>
        <div class="header-actions">
          <!-- Botões de Alternância -->
          <div class="toggle-buttons">
            <button
              class="btn toggle-btn"
              :class="{ 'btn-primary': tipoSelecionado === 'fisica', 'btn-secondary': tipoSelecionado !== 'fisica' }"
              @click="alterarTipo('fisica')"
            >
              Pessoas Físicas
            </button>
            <button
              class="btn toggle-btn"
              :class="{ 'btn-primary': tipoSelecionado === 'juridica', 'btn-secondary': tipoSelecionado !== 'juridica' }"
              @click="alterarTipo('juridica')"
            >
              Pessoas Jurídicas
            </button>
          </div>
          <!-- Botão de Fechar -->
          <button class="close-btn" @click="$emit('fechar')">
            <i class="bi bi-x-circle"></i>
          </button>
        </div>
      </div>

      <!-- Barra de Pesquisa -->
      <div class="modal-filters">
        <label class="filter-label">Filtros:</label>
        <div class="filter-group">
          <input 
            type="text" 
            v-model="buscaNome" 
            :placeholder="abaSelecionada === 'fisica' ? 'Nome' : 'Razão Social'" 
            class="filter-input"
            @input="filtrarPessoas"
          />
          <input 
            type="text" 
            v-model="buscaCPF" 
            :placeholder="abaSelecionada === 'fisica' ? 'CPF' : 'CNPJ'" 
            class="filter-input"
            @input="filtrarPessoas"
          />
          <!-- Botão Limpar Filtros -->
          <b-button
            variant="secondary"
            @click="limparFiltros"
            class="filter-btn"
          >
            <i class="bi bi-funnel" title="Limpar Filtro"></i>
          </b-button>
        </div>
      </div>

      <!-- Tabela de Pessoas -->
      <div class="table-container">
        <table class="table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>CPF/CNPJ</th>
              <th>E-mail</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!pessoasFiltradas.length">
              <td colspan="4" class="text-center">Nenhum registro encontrado.</td>
            </tr>
            <tr v-else v-for="pessoa in pessoasFiltradas" :key="pessoa.id">
              <td>{{ pessoa.nome || pessoa.razao_social }}</td>
              <td>{{ pessoa.cpf || pessoa.cnpj || "N/A" }}</td>
              <td>{{ pessoa.email || "N/A" }}</td>
              <td>
                <button class="btn btn-success btn-sm" @click="selecionarPessoa(pessoa)">
                  <i class="bi bi-check-lg"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Rodapé com Paginação Centralizada -->
      <div class="modal-footer">
        <span class="total-registros">{{ totalRegistros }} registros</span>
        <div class="pagination-container">
          <button 
            class="btn btn-light pagination-btn" 
            :disabled="pagina <= 1" 
            @click="mudarPagina(pagina - 1)"
          >
            <i class="bi bi-chevron-left"></i>
          </button>
          <span class="pagination-text">Página {{ pagina }} de {{ totalPaginas }}</span>
          <button 
            class="btn btn-light pagination-btn" 
            :disabled="pagina >= totalPaginas" 
            @click="mudarPagina(pagina + 1)"
          >
            <i class="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from "vue";
import axios from "@/services/axios";
import { BButton } from "bootstrap-vue-next";

export default {
  components: { BButton },
  props: {
    mostrar: Boolean,
  },
  emits: ["fechar", "selecionar"],
  setup(props, { emit }) {
    const abaSelecionada = ref("fisica");
    const buscaNome = ref("");
    const buscaCPF = ref("");
    const pessoas = ref([]);
    const pagina = ref(1);
    const totalPaginas = ref(1);
    const totalRegistros = ref(0);
    const tipoSelecionado = ref("fisica");

    const carregarPessoas = async () => {
      try {
        const endpoint =
          tipoSelecionado.value === "fisica"
            ? `/cadastros/pessoas-fisicas`
            : `/cadastros/pessoas-juridicas`;

        const params = {
          page: pagina.value,
          nome: buscaNome.value || undefined,
        };

        if (tipoSelecionado.value === "fisica") {
          params.cpf = buscaCPF.value || undefined;
        } else {
          params.cnpj = buscaCPF.value || undefined;
        }

        const response = await axios.get(endpoint, { params });

        pessoas.value = response.data.data || [];
        totalPaginas.value = response.data.totalPages || 1;
        totalRegistros.value = response.data.totalRecords || 0;
      } catch (error) {
        console.error("Erro ao carregar pessoas:", error);
        pessoas.value = [];
      }
    };

    const filtrarPessoas = () => {
      pagina.value = 1;
      carregarPessoas();
    };

    const alterarTipo = (tipo) => {
      if (abaSelecionada.value !== tipo) {
        abaSelecionada.value = tipo;
        tipoSelecionado.value = tipo;
        pagina.value = 1;
        pessoas.value = [];
        carregarPessoas();
      }
    };

    const pessoasFiltradas = computed(() =>
      pessoas.value.filter((pessoa) =>
        (pessoa.nome?.toLowerCase().includes(buscaNome.value.toLowerCase()) ||
         pessoa.razao_social?.toLowerCase().includes(buscaNome.value.toLowerCase())) &&
        (!buscaCPF.value || pessoa.cpf?.includes(buscaCPF.value) || pessoa.cnpj?.includes(buscaCPF.value))
      )
    );

    const selecionarPessoa = (pessoa) => {
      emit("selecionar", { 
        id: pessoa.id, 
        nome: pessoa.nome || pessoa.razao_social, 
        tipo: tipoSelecionado.value 
      });
      emit("fechar");
    };

    const mudarPagina = (novaPagina) => {
      if (novaPagina > 0 && novaPagina <= totalPaginas.value) {
        pagina.value = novaPagina;
        carregarPessoas();
      }
    };

    const limparFiltros = () => {
      buscaNome.value = "";
      buscaCPF.value = "";
      carregarPessoas();
    };

    // Reseta os filtros e abas sempre que o modal for aberto
    watch(() => props.mostrar, (novoValor) => {
      if (novoValor) {
        abaSelecionada.value = "fisica";
        tipoSelecionado.value = "fisica";
        limparFiltros();
      }
    });

    onMounted(() => {
      if (props.mostrar) carregarPessoas();
    });

    return {
      abaSelecionada,
      buscaNome,
      buscaCPF,
      pessoasFiltradas,
      selecionarPessoa,
      limparFiltros,
      pagina,
      totalPaginas,
      totalRegistros,
      mudarPagina,
      tipoSelecionado,
      carregarPessoas,
      filtrarPessoas,
      alterarTipo,
    };
  },
};
</script>

<style scoped>
/* Estilos para o modal-overlay permanecem os mesmos */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Estilos do contêiner principal (modal-container) com tamanho fixo */
.modal-container {
  display: block;
  background: white;
  padding: 20px;
  border-radius: 8px;
  width: 60%;
  max-width: 700px;
  height: 600px; /* altura fixa */
  overflow-y: auto;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* Demais estilos (cabeçalho, filtros, tabela, rodapé) podem permanecer ou ser ajustados conforme necessário */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
  border-bottom: 1px solid #ddd;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 15px;
}

.toggle-buttons {
  display: flex;
  gap: 10px;
}

.toggle-btn {
  padding: 8px 15px;
  font-size: 14px;
  border-radius: 6px;
  transition: 0.2s;
}

.btn-primary {
  background-color: #0d6efd;
  color: white;
  border: 1px solid #0d6efd;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
  border: 1px solid #6c757d;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.4rem;
  cursor: pointer;
  color: #666;
  transition: 0.2s;
  margin-left: auto;
}

.close-btn:hover {
  color: #050505;
}

.modal-filters {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 15px;
}

.filter-label {
  font-size: 14px;
  font-weight: bold;
  color: #333;
}

.filter-group {
  display: flex;
  flex-grow: 1;
  gap: 10px;
  align-items: center;
}

.filter-input {
  flex: 1;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  transition: 0.2s;
}

.filter-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(0, 123, 255, 0.3);
}

.table-container {
  margin-top: 15px;
}

.table th, .table td {
  padding: 8px 10px;
  line-height: 1.2;
  vertical-align: middle;
}

.table td .btn-sm {
  padding: 4px 6px;
  font-size: 0.85rem;
  height: 20px;
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.table th {
  background: #f1f1f1;
  font-weight: bold;
}

.table td {
  padding: 8px;
  border-bottom: 1px solid #ddd;
}

.modal-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #ddd;
}

.pagination-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pagination-btn {
  padding: 6px 10px;
  font-size: 14px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-text {
  font-size: 12px;
  font-weight: normal;
  color: #333;
}

.total-registros {
  position: absolute;
  left: 20px;
  font-size: 14px;
  color: #555;
}

/* Estilização do botão de limpar filtros */
.filter-btn {
  background: #6c757d;
  border: none;
  color: white;
  padding: 6px 12px;
  font-size: 16px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.filter-btn i {
  font-size: 18px;
}

.filter-btn:hover {
  background: #1a1b1b;
}
</style>
